import React from 'react';
import { Box, Typography, Stack, Avatar, IconButton, Button } from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

/**
 * CafeCard
 *
 * Props:
 * @param {object} cafe - 카페 데이터 { id, name, description, magazineParticipant, userInfo } [Required]
 * @param {function} onMore - 더보기 클릭 핸들러 [Optional]
 * @param {boolean} showEditButton - 편집 버튼 표시 여부 [Optional]
 * @param {function} onEdit - 편집 클릭 핸들러 [Optional]
 * @param {boolean} showDeleteButton - 삭제 버튼 표시 여부 [Optional]
 * @param {function} onDelete - 삭제 클릭 핸들러 [Optional]
 * @param {boolean} showApprovalButtons - 승인/거절 버튼 표시 여부 [Optional]
 * @param {function} onApprove - 승인 클릭 핸들러 [Optional]
 * @param {function} onReject - 거절 클릭 핸들러 [Optional]
 *
 * Example usage:
 * <CafeCard cafe={cafe} onMore={()=>setDetailCafe(cafe)} showEditButton={true} onEdit={()=>editCafe(cafe)} showDeleteButton={true} onDelete={()=>deleteCafe(cafe)} />
 */
export default function CafeCard({ cafe, onMore, showEditButton = false, onEdit, showDeleteButton = false, onDelete, showApprovalButtons = false, onApprove, onReject }) {
  const name = cafe?.name || '';
  const description = cafe?.description || '';
  const fullAddress = cafe?.address || '';
  const shortAddress = fullAddress
    ? fullAddress.trim().split(/\s+/).slice(0, 2).join(' ')
    : '';

  const isParticipant = !!cafe?.magazineParticipant;
  const user = cafe?.userInfo || null;
  const username = user?.profile?.username || '';
  const avatarUrl = user?.profileImage || user?.profile?.threads_profile_picture_url || '';

  return (
    <Box
      onClick={showEditButton ? undefined : onMore}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundColor: 'background.paper',
        cursor: onMore && !showEditButton ? 'pointer' : 'default',
        transition: 'border-color 0.15s ease',
        '&:hover': { borderColor: 'text.primary' },
        position: 'relative',
      }}
    >
      <Box>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ height: 40 }}>
            {user ? (
              <Avatar src={avatarUrl || undefined} sx={{ width: 40, height: 40 }} />
            ) : null}
            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography
                variant="h6"
                noWrap
                title={name}
                sx={{ fontWeight: 800 }}
              >
                {name}
              </Typography>
            </Box>
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            title={description}
          >
            {description || '설명이 없습니다.'}
          </Typography>
          {/* 유저네임 영역도 항상 유지하여 카드 간 높이 정렬 */}
          <Box sx={{ mt: 0.5, minHeight: 16, display: 'flex', alignItems: 'center' }}>
            {username ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                @{username}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 2.5 }}>
          <LocationOnOutlinedIcon sx={{ fontSize: 16, color: 'text.primary' }} />
          <Typography variant="caption" color="text.primary" title={fullAddress} noWrap>
            {shortAddress || '-'}
          </Typography>
        </Stack>
      </Box>

      {/* 편집 버튼 */}
      {showEditButton && onEdit && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: showDeleteButton ? 48 : 8, // 삭제 버튼이 있으면 왼쪽으로 이동
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'text.primary',
            },
          }}
          size="small"
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      )}

      {/* 삭제 버튼 */}
      {showDeleteButton && onDelete && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: 'error.light',
              borderColor: 'error.main',
              color: 'error.contrastText',
            },
          }}
          size="small"
        >
          <DeleteOutlinedIcon fontSize="small" />
        </IconButton>
      )}

      {/* 승인/거절 버튼 */}
      {showApprovalButtons && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onApprove && onApprove();
            }}
            fullWidth
          >
            승인
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<CancelOutlinedIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onReject && onReject();
            }}
            fullWidth
          >
            거절
          </Button>
        </Stack>
      )}

      {/* 더보기 버튼 (편집/삭제/승인 모드가 아닐 때만) */}
      {!showEditButton && !showDeleteButton && !showApprovalButtons && onMore && (
        <Box
          onClick={onMore}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            cursor: 'pointer',
            zIndex: 1,
          }}
        />
      )}
    </Box>
  );
}


